import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Form,
  Button,
  Card,
  Row,
  Col,
  Alert,
  Spinner,
  Table,
  InputGroup
} from 'react-bootstrap';
import { FaPlus, FaMinus, FaSave, FaArrowLeft } from 'react-icons/fa';

const EditBill = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    patientId: '',
    doctorId: '',
    billDate: new Date().toISOString().split('T')[0],
    paymentStatus: 'pending',
    paymentMethod: '',
    paidAmount: 0,
    totalAmount: 0,
    discount: 0,
    tax: 0,
    notes: ''
  });
  
  // Bill items
  const [items, setItems] = useState([
    { description: '', quantity: 1, unitPrice: 0, amount: 0 }
  ]);
  
  // Doctor fee state
  const [includeDoctorFee, setIncludeDoctorFee] = useState(true);
  const [doctorFee, setDoctorFee] = useState({
    regular: 1000,
    specialist: 1500,
    consultant: 2000
  });
  
  // Fetch bill data, patients and doctors
  useEffect(() => {
    const fetchData = async () => {
      try {
        // First fetch the bill data
        const billRes = await axios.get(`/api/billings.php?id=${id}`);
        const billData = billRes.data;
        
        console.log("Loaded bill data:", billData);
        console.log("Patient ID from bill:", billData.patientId);
        console.log("Doctor ID from bill:", billData.doctorId);
        
        // Extract bill items
        let billItems = [];
        if (billData.billItems && Array.isArray(billData.billItems)) {
          billItems = billData.billItems;
        } else if (billData.BillItems && Array.isArray(billData.BillItems)) {
          billItems = billData.BillItems;
        } else if (typeof billData.billItems === 'string') {
          try {
            billItems = JSON.parse(billData.billItems);
          } catch (e) {
            console.error("Failed to parse billItems:", e);
            billItems = [{ description: 'Consultation', quantity: 1, unitPrice: billData.totalAmount, amount: billData.totalAmount }];
          }
        }
        
        // If no items found, create default
        if (!billItems || billItems.length === 0) {
          billItems = [{ description: 'Consultation', quantity: 1, unitPrice: billData.totalAmount, amount: billData.totalAmount }];
        }
        
        // Check if there's already a doctor fee item
        const hasDoctorFeeItem = billItems.some(item => 
          item.description.toLowerCase().includes('doctor fee') || 
          item.description.toLowerCase().includes('consultation fee')
        );
        
        // Set includeDoctorFee based on whether there's already a doctor fee item and a doctor is selected
        setIncludeDoctorFee(hasDoctorFeeItem && billData.doctorId);
        
        // Set bill items
        setItems(billItems);
        
        // Set form data
        const billDateValue = billData.billDate || billData.date || billData.createdAt;
        let formattedBillDate = new Date().toISOString().split('T')[0]; // Default to today
        
        if (billDateValue) {
          try {
            const dateObj = new Date(billDateValue);
            if (!isNaN(dateObj.getTime())) {
              formattedBillDate = dateObj.toISOString().split('T')[0];
            }
          } catch (e) {
            console.error('Error parsing bill date:', e);
          }
        }
        
        setFormData({
          patientId: billData.patientId || '',
          doctorId: billData.doctorId || '',
          billDate: formattedBillDate,
          paymentStatus: billData.paymentStatus || 'pending',
          paymentMethod: billData.paymentMethod || '',
          paidAmount: billData.paidAmount || 0,
          totalAmount: billData.totalAmount || 0,
          discount: billData.discount || 0,
          tax: billData.tax || 0,
          notes: billData.notes || ''
        });
        
        // Fetch patients and doctors for dropdowns
        const [patientsRes, doctorsRes] = await Promise.all([
          axios.get('/api/patients'),
          axios.get('/api/doctors')
        ]);
        
        setPatients(patientsRes.data);
        setDoctors(doctorsRes.data);
        setError(null);
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load bill data. Please try again.');
      } finally {
        setInitialLoading(false);
      }
    };
    
    fetchData();
  }, [id]);
  
  // Helper function to add or update doctor fee
  const addOrUpdateDoctorFee = (doctorId) => {
    const selectedDoctor = doctors.find(doctor => String(doctor.id) === String(doctorId));
    console.log("Selected doctor:", selectedDoctor);
    
    if (selectedDoctor) {
      let fee = parseFloat(selectedDoctor.consultationFee) || parseFloat(doctorFee.regular);
      console.log("Doctor fee:", fee);
      
      if (!selectedDoctor.consultationFee) {
        if (selectedDoctor.specialization && selectedDoctor.specialization.toLowerCase().includes('specialist') || 
            selectedDoctor.specialization && selectedDoctor.specialization.toLowerCase().includes('surgeon')) {
          fee = parseFloat(doctorFee.specialist);
        } else if (selectedDoctor.specialization && selectedDoctor.specialization.toLowerCase().includes('consultant')) {
          fee = parseFloat(doctorFee.consultant);
        }
        console.log("Adjusted fee based on specialization:", fee);
      }
      
      const doctorFeeItemIndex = items.findIndex(item => 
        item.description.toLowerCase().includes('doctor fee') || 
        item.description.toLowerCase().includes('consultation fee')
      );
      console.log("Doctor fee item index:", doctorFeeItemIndex);
      
      let updatedItems;
      if (doctorFeeItemIndex >= 0) {
        updatedItems = [...items];
        updatedItems[doctorFeeItemIndex] = {
          description: `Doctor Fee (${selectedDoctor.name})`,
          quantity: 1,
          unitPrice: fee,
          amount: fee
        };
        console.log("Updated existing fee item:", updatedItems[doctorFeeItemIndex]);
      } else {
        updatedItems = [
          ...items,
          {
            description: `Doctor Fee (${selectedDoctor.name})`,
            quantity: 1,
            unitPrice: fee,
            amount: fee
          }
        ];
        console.log("Added new fee item:", updatedItems[updatedItems.length - 1]);
      }
      
      setItems(updatedItems);
      
      const subtotal = Math.round(updatedItems.reduce((sum, item) => sum + item.amount, 0) * 100) / 100;
      const discountAmount = Math.round(subtotal * (formData.discount / 100) * 100) / 100;
      const taxAmount = Math.round((subtotal - discountAmount) * (formData.tax / 100) * 100) / 100;
      const total = Math.round((subtotal - discountAmount + taxAmount) * 100) / 100;
      
      setFormData(prev => ({
        ...prev,
        totalAmount: total
      }));
    }
  };

  // Add a new function to handle doctor selection and fee calculation
  const handleDoctorChange = (e) => {
    const doctorId = e.target.value;
    console.log("Doctor changed to:", doctorId, "includeDoctorFee:", includeDoctorFee);
    
    // Update the formData with the selected doctorId
    setFormData(prev => ({
      ...prev,
      doctorId
    }));
    
    // If a doctor is selected and includeDoctorFee is true, add or update the doctor fee item
    if (doctorId && includeDoctorFee) {
      addOrUpdateDoctorFee(doctorId);
    }
  };

  // Handle doctor fee toggle
  const handleDoctorFeeToggle = (e) => {
    const shouldInclude = e.target.checked;
    console.log("Doctor fee toggle changed to:", shouldInclude);
    setIncludeDoctorFee(shouldInclude);
    
    if (shouldInclude && formData.doctorId) {
      console.log("Should add doctor fee for doctor:", formData.doctorId);
      // If toggling on and doctor is selected, add fee immediately
      addOrUpdateDoctorFee(formData.doctorId);
    } else if (!shouldInclude) {
      console.log("Removing doctor fee items");
      // If toggling off, remove doctor fee item
      const updatedItems = items.filter(item => 
        !item.description.toLowerCase().includes('doctor fee') && 
        !item.description.toLowerCase().includes('consultation fee')
      );
      console.log("Items after removing doctor fee:", updatedItems);
      setItems(updatedItems);
      
      // Recalculate total with rounding
      const subtotal = Math.round(updatedItems.reduce((sum, item) => sum + item.amount, 0) * 100) / 100;
      const discountAmount = Math.round(subtotal * (formData.discount / 100) * 100) / 100;
      const taxAmount = Math.round((subtotal - discountAmount) * (formData.tax / 100) * 100) / 100;
      const total = Math.round((subtotal - discountAmount + taxAmount) * 100) / 100;
      
      setFormData(prev => ({
        ...prev,
        totalAmount: total
      }));
    }
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for doctor selection
    if (name === 'doctorId') {
      handleDoctorChange(e);
      return;
    }
    
    // Handle numeric fields
    if (name === 'paidAmount' || name === 'tax' || name === 'discount') {
      const numericValue = parseFloat(value) || 0;
      
      setFormData(prev => {
        const updatedFormData = {
          ...prev,
          [name]: numericValue
        };
        
        // If tax or discount changed, recalculate total amount
        if (name === 'tax' || name === 'discount') {
          // Calculate subtotal from items
          const subtotal = items.reduce((sum, item) => sum + item.amount, 0);
          
          // Calculate discount amount
          const discountAmount = subtotal * (name === 'discount' ? numericValue / 100 : prev.discount / 100);
          
          // Calculate tax amount (applied after discount)
          const taxAmount = (subtotal - discountAmount) * (name === 'tax' ? numericValue / 100 : prev.tax / 100);
          
          // Calculate total (subtotal - discount + tax)
          updatedFormData.totalAmount = subtotal - discountAmount + taxAmount;
        }
        
        // Auto-calculate payment status when paidAmount changes
        if (name === 'paidAmount') {
          const total = prev.totalAmount;
          if (numericValue >= total) {
            updatedFormData.paymentStatus = 'complete';
          } else if (numericValue > 0) {
            updatedFormData.paymentStatus = 'partial';
          } else {
            updatedFormData.paymentStatus = 'pending';
          }
        }
        
        return updatedFormData;
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };
  
  // Handle bill item changes
  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    
    // Parse numeric values
    if (field === 'quantity' || field === 'unitPrice') {
      newItems[index][field] = parseFloat(value) || 0;
    } else {
      newItems[index][field] = value;
    }
    
    // Calculate amount with rounding
    newItems[index].amount = Math.round(
      newItems[index].quantity * newItems[index].unitPrice * 100
    ) / 100;
    
    setItems(newItems);
    
    // Calculate subtotal with rounding
    const subtotal = Math.round(
      newItems.reduce((sum, item) => sum + item.amount, 0) * 100
    ) / 100;
    
    // Calculate discount amount
    const discountAmount = Math.round(subtotal * (formData.discount / 100) * 100) / 100;
    
    // Calculate tax amount (applied after discount)
    const taxAmount = Math.round((subtotal - discountAmount) * (formData.tax / 100) * 100) / 100;
    
    // Calculate total (subtotal - discount + tax)
    const total = Math.round((subtotal - discountAmount + taxAmount) * 100) / 100;
    
    setFormData(prev => ({
      ...prev,
      totalAmount: total
    }));
  };
  
  // Add new item
  const addItem = () => {
    setItems([
      ...items,
      { description: '', quantity: 1, unitPrice: 0, amount: 0 }
    ]);
  };
  
  // Remove item
  const removeItem = (index) => {
    if (items.length === 1) return;
    
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
    
    // Recalculate total with rounding
    const subtotal = Math.round(
      newItems.reduce((sum, item) => sum + item.amount, 0) * 100
    ) / 100;
    
    const discountAmount = Math.round(subtotal * (formData.discount / 100) * 100) / 100;
    const taxAmount = Math.round((subtotal - discountAmount) * (formData.tax / 100) * 100) / 100;
    const total = Math.round((subtotal - discountAmount + taxAmount) * 100) / 100;
    
    setFormData(prev => ({
      ...prev,
      totalAmount: total
    }));
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Update the bill with items
      await axios.put(`/api/billings.php?id=${id}`, {
        ...formData,
        billItems: items
      });
      
      setSuccess(true);
      setError(null);
      
      // Redirect after successful update
      setTimeout(() => {
        navigate(`/billing/${id}`);
      }, 1500);
    } catch (err) {
      console.error('Error updating bill:', err);
      setError('Failed to update bill. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  if (initialLoading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading bill data...</p>
      </div>
    );
  }
  
  return (
    <div className="my-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Edit Bill</h2>
        <Button variant="outline-secondary" onClick={() => navigate(`/billing/${id}`)}>
          <FaArrowLeft className="me-2" /> Back to Bill
        </Button>
      </div>
      
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">Bill updated successfully!</Alert>}
      
      <Form onSubmit={handleSubmit}>
        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">Bill Information</h5>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Patient *</Form.Label>
                  <Form.Select
                    name="patientId"
                    value={formData.patientId}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Patient</option>
                    {patients.map(patient => (
                      <option key={patient.id} value={patient.id}>
                        {patient.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Doctor</Form.Label>
                  <Form.Select
                    name="doctorId"
                    value={formData.doctorId}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Doctor (optional)</option>
                    {doctors.map(doctor => (
                      <option key={doctor.id} value={doctor.id}>
                        {doctor.name} ({doctor.specialization})
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                
                {formData.doctorId && (
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      id="include-doctor-fee"
                      label="Include doctor's fee automatically"
                      checked={includeDoctorFee}
                      onChange={handleDoctorFeeToggle}
                    />
                  </Form.Group>
                )}
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Bill Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="billDate"
                    value={formData.billDate}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Payment Status (Auto-calculated)</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.paymentStatus.charAt(0).toUpperCase() + formData.paymentStatus.slice(1)}
                    readOnly
                    disabled
                    className="bg-light"
                  />
                  <Form.Text className="text-muted">
                    Status automatically updates based on amount paid
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Payment Method</Form.Label>
                  <Form.Select
                    name="paymentMethod"
                    value={formData.paymentMethod}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Payment Method</option>
                    <option value="cash">Cash</option>
                    <option value="card">Credit/Debit Card</option>
                    <option value="insurance">Insurance</option>
                    <option value="bank">Bank Transfer</option>
                    <option value="other">Other</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Amount Paid</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>Rs.</InputGroup.Text>
                    <Form.Control
                      type="number"
                      name="paidAmount"
                      value={formData.paidAmount}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>
            
            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                placeholder="Additional notes about this bill"
              />
            </Form.Group>
          </Card.Body>
        </Card>
        
        <Card className="mb-4">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Bill Items</h5>
            <Button 
              variant="success" 
              size="sm" 
              onClick={addItem}
            >
              <FaPlus className="me-1" /> Add Item
            </Button>
          </Card.Header>
          <Card.Body>
            <Table responsive>
              <thead>
                <tr>
                  <th style={{ width: '40%' }}>Description</th>
                  <th style={{ width: '15%' }}>Quantity</th>
                  <th style={{ width: '20%' }}>Unit Price</th>
                  <th style={{ width: '20%' }}>Amount</th>
                  <th style={{ width: '5%' }}></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => (
                  <tr key={index}>
                    <td>
                      <Form.Control
                        type="text"
                        value={item.description}
                        onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                        placeholder="Service or item description"
                        required
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="number"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                        min="1"
                        required
                      />
                    </td>
                    <td>
                      <InputGroup>
                        <InputGroup.Text>Rs.</InputGroup.Text>
                        <Form.Control
                          type="number"
                          value={item.unitPrice}
                          onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                          min="0"
                          step="0.01"
                          required
                        />
                      </InputGroup>
                    </td>
                    <td>
                      <InputGroup>
                        <InputGroup.Text>Rs.</InputGroup.Text>
                        <Form.Control
                          type="number"
                          value={item.amount}
                          readOnly
                          disabled
                        />
                      </InputGroup>
                    </td>
                    <td>
                      <Button 
                        variant="danger" 
                        size="sm" 
                        onClick={() => removeItem(index)}
                        disabled={items.length === 1}
                      >
                        <FaMinus />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="3" className="text-end fw-bold">Subtotal:</td>
                  <td>
                    <InputGroup>
                      <InputGroup.Text>Rs.</InputGroup.Text>
                      <Form.Control
                        type="number"
                        value={Math.round(items.reduce((sum, item) => sum + item.amount, 0) * 100) / 100}
                        readOnly
                        disabled
                      />
                    </InputGroup>
                  </td>
                  <td></td>
                </tr>
                <tr>
                  <td colSpan="2" className="text-end fw-bold">Discount:</td>
                  <td>
                    <InputGroup>
                      <Form.Control
                        type="number"
                        name="discount"
                        value={formData.discount}
                        onChange={handleInputChange}
                        min="0"
                        step="0.01"
                      />
                      <InputGroup.Text>%</InputGroup.Text>
                    </InputGroup>
                  </td>
                  <td>
                    <InputGroup>
                      <InputGroup.Text>Rs.</InputGroup.Text>
                      <Form.Control
                        type="number"
                        value={Math.round((Math.round(items.reduce((sum, item) => sum + item.amount, 0) * 100) / 100) * formData.discount / 100 * 100) / 100}
                        readOnly
                        disabled
                      />
                    </InputGroup>
                  </td>
                  <td></td>
                </tr>
                <tr>
                  <td colSpan="2" className="text-end fw-bold">Tax:</td>
                  <td>
                    <InputGroup>
                      <Form.Control
                        type="number"
                        name="tax"
                        value={formData.tax}
                        onChange={handleInputChange}
                        min="0"
                        step="0.01"
                      />
                      <InputGroup.Text>%</InputGroup.Text>
                    </InputGroup>
                  </td>
                  <td>
                    <InputGroup>
                      <InputGroup.Text>Rs.</InputGroup.Text>
                      <Form.Control
                        type="number"
                        value={(() => {
                          const subtotal = Math.round(items.reduce((sum, item) => sum + item.amount, 0) * 100) / 100;
                          const discountAmount = Math.round(subtotal * formData.discount / 100 * 100) / 100;
                          return Math.round((subtotal - discountAmount) * formData.tax / 100 * 100) / 100;
                        })()}
                        readOnly
                        disabled
                      />
                    </InputGroup>
                  </td>
                  <td></td>
                </tr>
                <tr>
                  <td colSpan="3" className="text-end fw-bold">Total:</td>
                  <td>
                    <InputGroup>
                      <InputGroup.Text>Rs.</InputGroup.Text>
                      <Form.Control
                        type="number"
                        value={formData.totalAmount}
                        readOnly
                        disabled
                      />
                    </InputGroup>
                  </td>
                  <td></td>
                </tr>
                <tr>
                  <td colSpan="3" className="text-end fw-bold">Amount Paid:</td>
                  <td>
                    <InputGroup>
                      <InputGroup.Text>Rs.</InputGroup.Text>
                      <Form.Control
                        type="number"
                        value={formData.paidAmount}
                        readOnly
                        disabled
                      />
                    </InputGroup>
                  </td>
                  <td></td>
                </tr>
                <tr>
                  <td colSpan="3" className="text-end fw-bold">Balance:</td>
                  <td>
                    <InputGroup>
                      <InputGroup.Text>Rs.</InputGroup.Text>
                      <Form.Control
                        type="number"
                        value={Math.max(0, formData.totalAmount - formData.paidAmount)}
                        className={formData.totalAmount - formData.paidAmount > 0 ? 'text-danger' : 'text-success'}
                        readOnly
                        disabled
                      />
                    </InputGroup>
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </Table>
          </Card.Body>
        </Card>
        
        <div className="d-flex justify-content-end">
          <Button 
            variant="secondary" 
            className="me-2" 
            onClick={() => navigate(`/billing/${id}`)}
          >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            type="submit" 
            disabled={loading || success}
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Updating...
              </>
            ) : (
              <>
                <FaSave className="me-2" /> Save Changes
              </>
            )}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default EditBill; 
